/**
 * AriaMarkdown — line-by-line markdown renderer for AI assistant output.
 *
 * Extracted from AIAssistant.jsx (sprint F2). No external deps so the bundle
 * stays small and every rendered token can be mapped to a styling decision.
 *
 * Vive en `shared` y no en `ai/` porque lo pintan también los paneles de ARIA
 * de cada dominio (OCR, lienzo contable, nóminas), y ningún dominio puede
 * importar de `ai` (`.dependency-cruiser.cjs`, `nadie-depende-de-ai`). Lo que
 * sí es de ARIA —la barra de fuentes con departamentos— se queda en
 * `ai/v2/MessageMarkdown.jsx` y entra por `footer`.
 *
 * Supports: headings (H1-H4), paragraphs, bold/italic/code/strike, links
 * (external + internal /path), unordered + ordered lists, blockquotes,
 * fenced code blocks, horizontal rules, pipe-delimited tables.
 *
 * Memoized via React.memo because the parent re-renders on every SSE token
 * of the surrounding assistant message. Content equality is enough — the
 * parent always rebuilds a fresh string when the content changes, so
 * reference equality on content acts as the memo key.
 */

import { memo } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, Lightbulb } from 'lucide-react';
import CopyButton from '@/components/common/CopyButton';
import { sanitizeURL } from '@/components/utils/sanitize';

function renderInline(text) {
  if (!text) return null;
  // Split on **bold**, _italic_, `code`, [link](url), ~~strike~~
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|~~[^~]+~~|_[^_\s][^_]*[^_\s]_|_[^_\s]_)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    // Recursivo en el contenido de negrita/italic/strike: un enlace o `code`
    // anidado (p.ej. `**[texto](url)**`) debe renderizarse, no salir literal.
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i} className="font-semibold text-foreground">{renderInline(part.slice(2, -2))}</strong>;
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={i} className="px-1 py-0.5 bg-muted rounded text-xs font-mono text-foreground">{part.slice(1, -1)}</code>;
    if (part.startsWith('~~') && part.endsWith('~~'))
      return <del key={i} className="text-muted-foreground">{renderInline(part.slice(2, -2))}</del>;
    if (part.startsWith('_') && part.endsWith('_') && part.length > 2)
      return <em key={i} className="italic text-muted-foreground">{renderInline(part.slice(1, -1))}</em>;
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [, label, rawHref] = linkMatch;
      // El href lo emite el modelo, y su entrada puede ser un documento que
      // acaba de subir el usuario (OcrChatSidebar) → prompt injection. React 18
      // solo avisa por consola ante `javascript:`, y en build de producción ni
      // eso, así que la validación tiene que ser nuestra. sanitizeURL admite
      // http/https/mailto/tel y rutas relativas, y rechaza `//host` (que
      // `startsWith('/')` dejaba pasar al window.location.href de abajo).
      const href = sanitizeURL(rawHref);
      if (!href) return label;
      if (href.startsWith('/')) {
        // El modelo emite rutas de raíz de la SPA ("/facturas/123"); la app
        // vive bajo import.meta.env.BASE_URL ("/app/"), y hace falta en el
        // href real (no solo en el onClick) para que abrir en pestaña nueva
        // o sin JS también resuelva al sitio correcto.
        const appHref = `${import.meta.env.BASE_URL.replace(/\/$/, '')}${href}`;
        return <a key={i} href={appHref} className="text-violet-600 dark:text-violet-400 underline underline-offset-2 cursor-pointer" onClick={(e) => { e.preventDefault(); window.location.href = appHref; }}>{label}</a>;
      }
      return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-violet-600 dark:text-violet-400 underline underline-offset-2">{label}</a>;
    }
    return part;
  });
}

// Avisos: el modelo marca con un emoticono al inicio de línea un aviso, un
// problema, algo correcto o una recomendación. Los emoticonos se ven distintos
// en cada sistema y desentonan con los iconos de línea del ERP, así que estos
// cinco se pintan con su icono lucide y su token semántico. El vocabulario es
// el mismo que fija el prompt de ARIA (verix-api `systemPrompt.ts`, FORMATO).
const CALLOUTS = {
  '⚠': { Icon: AlertTriangle, label: 'Aviso', tone: 'border-warning/40 bg-warning/10', icon: 'text-warning' },
  '🔴': { Icon: AlertCircle, label: 'Problema', tone: 'border-destructive/40 bg-destructive/10', icon: 'text-destructive' },
  '✅': { Icon: CheckCircle2, label: 'Correcto', tone: 'border-success/40 bg-success/10', icon: 'text-success' },
  '💡': { Icon: Lightbulb, label: 'Recomendación', tone: 'border-info/40 bg-info/10', icon: 'text-info' },
  'ℹ': { Icon: Info, label: 'Información', tone: 'border-info/40 bg-info/10', icon: 'text-info' },
};
// Emoticono(s) al inicio, con su selector de variación y uniones ZWJ.
const LEADING_EMOJI = /^(?:\p{Extended_Pictographic}️?‍?)+\s*/u;

/**
 * Separa el emoticono inicial de una línea. Admite que venga dentro de la
 * negrita (`**⚠️ Señal crítica:**`). Devuelve el aviso si es uno de los del
 * vocabulario, y el texto sin el emoticono; los decorativos (📊, 📈…) se quitan.
 */
function splitLeadingEmoji(text) {
  const bold = text.startsWith('**') ? '**' : '';
  const rest = text.slice(bold.length);
  const m = rest.match(LEADING_EMOJI);
  if (!m) return { callout: null, text };
  const first = String.fromCodePoint(m[0].codePointAt(0));
  return { callout: CALLOUTS[first] ?? null, text: bold + rest.slice(m[0].length) };
}

// El icono es decorativo; el tipo de aviso se dice en texto para lectores de
// pantalla, que antes al menos leían el emoticono.
function CalloutIcon({ callout, className = 'h-4 w-4' }) {
  const { Icon } = callout;
  return (
    <>
      <Icon className={`${className} shrink-0 ${callout.icon}`} aria-hidden="true" />
      <span className="sr-only">{callout.label}: </span>
    </>
  );
}

function Callout({ callout, children }) {
  return (
    <div role="note" className={`my-2 flex items-start gap-2 rounded-md border px-3 py-2 ${callout.tone}`}>
      <CalloutIcon callout={callout} className="mt-[3px] h-4 w-4" />
      <div className="min-w-0 flex-1 text-[15px] leading-relaxed text-foreground">{children}</div>
    </div>
  );
}

function Heading({ as: Tag, className, text }) {
  const { callout, text: clean } = splitLeadingEmoji(text);
  return (
    <Tag className={`${className} flex items-center gap-1.5`}>
      {callout && <CalloutIcon callout={callout} />}
      <span>{renderInline(clean)}</span>
    </Tag>
  );
}

// Celda de tabla: el modelo también marca estados con el emoticono al inicio de
// la celda («🔴 Sin cuenta registrada»), aunque el prompt lo limite al inicio de
// línea. Se pinta igual que en las listas: icono de línea delante del texto.
function renderCell(cell) {
  const { callout, text } = splitLeadingEmoji(cell);
  return (
    <>
      {callout && <CalloutIcon callout={callout} className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />}
      {renderInline(text)}
    </>
  );
}

function parseTableRow(row) {
  return row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
}

// Normaliza contenido del modelo cuando markdown estructural aparece embebido
// en mitad de una línea. Caso degradado en producción (ver captura del
// usuario el 2026-05-08): respuestas mixtas que tienen `\n` en los párrafos
// iniciales pero la zona de tabla viene aplastada como
// "Aquí el resultado: --- ### Gastos | Fecha | Concepto | …"
//
// La versión previa hacía early-return si el contenido tenía CUALQUIER `\n`,
// así que no actuaba en casos mixtos como el de la captura. Esta versión usa
// lookbehind `(?<!\n)` para insertar `\n` antes de los marcadores SOLO si no
// hay uno ya. Idempotente: aplicarla 2× no añade saltos extra.
//
// Markers cubiertos (alta señal — no falsos positivos en texto narrativo):
//   ` ### `         →  `\n### `        (cabeceras 1-4 con espacio antes)
//   ` --- `         →  `\n\n---\n\n`    (regla horizontal stand-alone)
//   ` |x|y|`        →  `\n|x|y|`        (fila de tabla embebida — ≥2 pipes)
//
// NO normalizamos `- ` ni `> ` porque tienen muchos falsos positivos en
// texto narrativo ("el coste es 1500 - 800", citas con `>`).
function normalizeFlattenedMarkdown(content) {
  if (!content) return content;
  let s = content;
  s = s.replace(/(?<!\n)\s+(#{1,4}\s)/g, '\n$1');
  // Título pegado a la frase anterior sin espacio («…completa.## Título»).
  s = s.replace(/([.:!?…)])(#{1,4}\s)/g, '$1\n$2');
  s = s.replace(/(?<!\n)\s+---+\s+(?!\n)/g, '\n\n---\n\n');
  s = s.replace(/(?<!\n)\s+(\|[^|\n]*\|[^|\n]*\|)/g, '\n$1');
  return s;
}

// Split normalized markdown into top-level blocks (one string each), grouping
// consecutive lines the way renderBlock expects. Block boundaries are stable
// while streaming: once a block is followed by a new one its string stops
// changing, so the memoized <MarkdownBlock> skips re-parsing — only the last,
// still-growing block re-renders per token instead of the whole message.
function splitBlocks(normalized) {
  const lines = normalized.split('\n');
  const blocks = [];
  let i = 0;
  const isStructural = (l) =>
    !l.trim() || /^#{1,4} /.test(l) || l.trim().startsWith('|') ||
    /^[-*+•] /.test(l) || /^\d+\. /.test(l) || l.startsWith('> ') ||
    /^[-*_]{3,}\s*$/.test(l.trim()) || l.startsWith('```');

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const start = i;

    if (line.startsWith('```')) {
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) i++;
      if (i < lines.length) i++; // closing fence (may be absent mid-stream)
    } else if (line.trim().startsWith('|')) {
      while (i < lines.length && lines[i].trim().startsWith('|')) i++;
    } else if (line.startsWith('> ')) {
      while (i < lines.length && lines[i].startsWith('> ')) i++;
    } else if (/^[-*+•] /.test(line)) {
      while (i < lines.length && /^[-*+•] /.test(lines[i])) i++;
    } else if (/^\d+\. /.test(line)) {
      while (i < lines.length && /^\d+\. /.test(lines[i])) i++;
    } else if (/^#{1,4} /.test(line) || /^[-*_]{3,}\s*$/.test(line.trim())) {
      i++; // heading / horizontal rule — single line
    } else {
      while (i < lines.length && !isStructural(lines[i])) i++; // paragraph
    }
    blocks.push(lines.slice(start, i).join('\n'));
  }
  return blocks;
}

// Render one pre-grouped block to a React element. Pure function of the block
// string → safe to memoize per block.
function renderBlock(block) {
  const lines = block.split('\n');
  const line = lines[0];

  // Escala sobria para un chat: el cuerpo va a 15-16 px y los títulos solo
  // destacan un paso por encima, sin bordes.
  if (/^#### /.test(line)) return <Heading as="h4" className="text-[15px] font-medium mt-2 mb-0.5 text-foreground" text={line.slice(5)} />;
  if (/^### /.test(line))  return <Heading as="h3" className="text-[15px] sm:text-base font-semibold mt-3 mb-1 text-foreground" text={line.slice(4)} />;
  if (/^## /.test(line))   return <Heading as="h2" className="text-base sm:text-[17px] font-semibold mt-3 mb-1 text-foreground" text={line.slice(3)} />;
  if (/^# /.test(line))    return <Heading as="h1" className="text-lg font-semibold mt-3 mb-1 text-foreground" text={line.slice(2)} />;

  if (/^[-*_]{3,}\s*$/.test(line.trim())) return <hr className="border-border my-2" />;

  // Fenced code block (closing fence may be absent while streaming → render
  // everything after the opening fence as code until it arrives).
  if (line.startsWith('```')) {
    const last = lines[lines.length - 1];
    const closed = lines.length > 1 && last.startsWith('```');
    const codeLines = lines.slice(1, closed ? -1 : undefined);
    const lang = line.slice(3).trim(); // lenguaje del fence (```sql) si lo hay
    const code = codeLines.join('\n');
    // B5 — badge de lenguaje + copiar al hover. Resaltado por tokens (highlight.js)
    // se omite a propósito: ARIA rara vez emite código y no compensa la dep.
    return (
      <div className="relative group my-1.5">
        <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {lang && <span className="text-[9px] uppercase tracking-wide text-muted-foreground px-1">{lang}</span>}
          <CopyButton text={code} label="Copiar código" className="h-5 w-5 bg-gray-800/80 hover:bg-gray-700" />
        </div>
        <pre className="p-3 bg-gray-900 dark:bg-black rounded-lg text-sm font-mono overflow-x-auto text-gray-100 leading-relaxed">
          <code>{code}</code>
        </pre>
      </div>
    );
  }

  // Pipe-delimited table.
  if (line.trim().startsWith('|')) {
    // Stream-safe: a lone header row (no separator/data yet) renders as text
    // until the rest of the table arrives, avoiding a one-column flash.
    if (lines.length < 2) {
      return <p className="text-[15px] sm:text-base leading-relaxed mb-1.5 last:mb-0">{renderInline(line)}</p>;
    }
    const isSep = (l) => /^\|[\s\-:|]+\|/.test(l.trim());
    const nonSep = lines.filter(l => !isSep(l));
    const [headerLine, ...dataLines] = nonSep;
    const headers = parseTableRow(headerLine || '');
    // Celda "numérica" (importe/porcentaje/contador) → alineada a la derecha en
    // mono tabular, como el resto de cifras del ERP. Se decide POR COLUMNA
    // (mayoría de celdas de datos numéricas) para que la columna entera respire
    // igual aunque tenga algún "—" suelto.
    const isNumericCell = (c) => /^[\s]*[-−]?[\d.,\s]+\s*(€|%|EUR)?[\s]*$/.test(String(c).replace(/\*\*/g, '').trim()) && /\d/.test(c);
    const parsedRows = dataLines.map((row) => parseTableRow(row));
    const numericCol = headers.map((_, ci) => {
      const cells = parsedRows.map((r) => r[ci]).filter((c) => c != null && String(c).trim() !== '' && String(c).trim() !== '—');
      return cells.length > 0 && cells.filter(isNumericCell).length >= Math.ceil(cells.length / 2);
    });
    return (
      <div className="overflow-x-auto my-2 rounded-lg border border-border text-sm">
        <table className="min-w-full border-collapse">
          <thead className="bg-primary/[0.06] dark:bg-primary/15">
            <tr>
              {headers.map((h, hi) => (
                <th key={hi} className={`px-2.5 py-1.5 font-semibold text-foreground whitespace-nowrap border-b border-border ${numericCol[hi] ? 'text-right' : 'text-left'}`}>{renderCell(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {parsedRows.map((cells, ri) => (
              <tr key={ri} className={ri % 2 === 1 ? 'bg-muted' : 'bg-card'}>
                {cells.map((cell, ci) => (
                  <td key={ci} className={`px-2.5 py-1.5 border-t border-border ${numericCol[ci] ? 'text-right font-mono tabular-nums whitespace-nowrap text-foreground' : 'text-muted-foreground'}`}>{renderCell(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (line.startsWith('> ')) {
    const qLines = lines.map(l => l.replace(/^> ?/, ''));
    const { callout, text: first } = splitLeadingEmoji(qLines[0]);
    if (callout) {
      return (
        <Callout callout={callout}>
          {[first, ...qLines.slice(1)].map((q, qi) => <p key={qi}>{renderInline(q)}</p>)}
        </Callout>
      );
    }
    return (
      <blockquote className="pl-3 border-l-2 border-violet-400 dark:border-violet-600 text-muted-foreground my-1.5">
        {qLines.map((q, qi) => <p key={qi} className="text-[15px] sm:text-base leading-relaxed">{renderInline(q)}</p>)}
      </blockquote>
    );
  }

  if (/^[-*+•] /.test(line)) {
    const items = lines.map(l => splitLeadingEmoji(l.replace(/^[-*+•] /, '')));
    return (
      <ul className="list-disc list-outside pl-4 space-y-1 mb-1.5 text-[15px] sm:text-base">
        {items.map(({ callout, text }, ii) => (
          // El icono ocupa la columna de la viñeta: el texto queda alineado con
          // el resto de elementos y el <li> no se sale de la lista.
          <li key={ii} className={`leading-relaxed ${callout ? 'relative list-none' : ''}`}>
            {callout && <CalloutIcon callout={callout} className="absolute -left-4 top-[5px] h-3.5 w-3.5" />}
            {renderInline(text)}
          </li>
        ))}
      </ul>
    );
  }

  if (/^\d+\. /.test(line)) {
    const items = lines.map(l => splitLeadingEmoji(l.replace(/^\d+\. /, '')));
    return (
      <ol className="list-decimal list-outside pl-4 space-y-1 mb-1.5 text-[15px] sm:text-base">
        {/* Aquí el número se queda: el icono va delante del texto. */}
        {items.map(({ callout, text }, ii) => (
          <li key={ii} className="leading-relaxed">
            {callout && <CalloutIcon callout={callout} className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />}
            {renderInline(text)}
          </li>
        ))}
      </ol>
    );
  }

  // Regular paragraph — wrapped lines joined with a space. Si empieza por uno
  // de los emoticonos de aviso, va en caja; uno decorativo se quita.
  const { callout, text: para } = splitLeadingEmoji(lines.join(' '));
  if (callout) return <Callout callout={callout}><p>{renderInline(para)}</p></Callout>;
  return <p className="text-[15px] sm:text-base leading-relaxed mb-1.5 last:mb-0">{renderInline(para)}</p>;
}

// Memoized per block: while streaming, completed blocks keep the same `text`
// and skip re-render; only the last growing block re-parses each token.
const MarkdownBlock = memo(
  function MarkdownBlock({ text }) { return renderBlock(text); },
  (prev, next) => prev.text === next.text,
);

export function renderMarkdown(content) {
  if (!content) return null;
  const blocks = splitBlocks(normalizeFlattenedMarkdown(content));
  if (blocks.length === 0) return null;
  return blocks.map((b, i) => <MarkdownBlock key={i} text={b} />);
}

/**
 * AriaMarkdown — renders markdown content of an AI assistant reply.
 * Extracts the trailing "_Datos consultados: X_" suffix into Fuentes chips,
 * unless the caller passes its own `footer` (ARIA's SourcesBar).
 *
 * Memoized on `content` so streaming SSE tokens only re-render the currently
 * streaming message, not every prior message in the conversation.
 *
 * Nota: antes existía un modo `compact` que reemplazaba contenido grande por
 * una tarjeta "Ver en Verix Gen-AI". Eliminado en fix/aria-widget-render-tables
 * porque rompía el widget: el usuario abría el chat desde la barra superior y
 * nunca veía las tablas. Ahora se renderiza todo igual; las tablas scrollean
 * horizontal cuando no caben (overflow-x-auto ya estaba aplicado en
 * renderMarkdown).
 */
function AriaMarkdownBase({ content, streaming = false, footer }) {
  const toolMatch = content?.match(/\n\n_Datos consultados: (.+?)_\s*$/);
  const cleanContent = toolMatch
    ? content.slice(0, content.length - toolMatch[0].length).trim()
    : (content || '');
  const toolsUsed = toolMatch?.[1] || null;

  return (
    <div>
      {renderMarkdown(cleanContent)}
      {/* Blinking caret while this message is still streaming. */}
      {streaming && (
        <span
          aria-hidden="true"
          className="inline-block w-[3px] h-3.5 ml-0.5 align-text-bottom rounded-sm bg-blue-500/70 animate-pulse"
        />
      )}
      {footer ?? (toolsUsed ? (
        <div className="mt-2 pt-1.5 border-t border-border flex items-center flex-wrap gap-1">
          <span className="text-[10px] text-muted-foreground">Fuentes:</span>
          {toolsUsed.split(', ').map((t, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded-full">{t}</span>
          ))}
        </div>
      ) : null)}
    </div>
  );
}

const AriaMarkdown = memo(
  AriaMarkdownBase,
  (prev, next) =>
    prev.content === next.content &&
    prev.streaming === next.streaming &&
    prev.footer === next.footer,
);
export default AriaMarkdown;
